import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import {
  FeeType,
  Fee,
  DomesticBaseFee,
  PaginatedResponse,
  SpeedFee,
  SpecialHandlingFee,
  DimensionalWeightFee,
} from "@/api.types";
import { z } from "zod";

const customerTierField = z.string().min(1, "Customer tier is required");

export type DomesticBaseFeeCreateFormSchema = z.infer<
  typeof domesticBaseFeeSchema
>;
export const domesticBaseFeeSchema = z.object({
  customerTierId: customerTierField,
  name: z.string().min(1, "Name is required"),
  basePrice: z.number().min(0, "Base price must be non-negative"),
  pickupServiceAreaId: z.string().min(1, "Pickup area is required"),
  deliveryServiceAreaId: z.string().min(1, "Delivery area is required"),
  isActive: z.boolean().default(true),
});

export type DimensionalWeightFeeCreateFormSchema = z.infer<
  typeof dimensionalWeightFeeSchema
>;
export const dimensionalWeightFeeSchema = z.object({
  customerTierId: customerTierField,
  name: z.string().min(1, "Name is required"),
  minWeight: z.number().min(0, "Minimum weight must be non-negative"),
  maxWeight: z.number().min(0, "Maximum weight must be non-negative"),
  baseDimWeight: z
    .number()
    .min(0, "Base dimensional weight must be non-negative"),
  pricePerKg: z.number().min(0, "Price per kg must be non-negative"),
  fee: z.number().min(0, "Fee must be non-negative"),
  maxSideLength: z.number().min(0, "Max side length must be non-negative"),
  factor: z.number().min(0, "Factor must be non-negative"),
  feeScope: z.enum(["DOMESTIC", "INTERNATIONAL", "ANY"]),
  isActive: z.boolean().default(true),
});

export type SpeedFeeCreateFormSchema = z.infer<typeof speedFeeSchema>;
export const speedFeeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["STANDARD", "EXPRESS", "SAME_DAY", "SCHEDULED"]),
  description: z.string().optional(),
  fee: z.number().min(0, "Fee must be non-negative"),
  feeScope: z.enum(["DOMESTIC", "INTERNATIONAL", "ANY"]),
  minDeliveryTime: z.number().int().nonnegative().optional(),
  maxDeliveryTime: z.number().int().nonnegative().optional(),
  timeUnit: z.enum(["HOURS", "DAYS"]).optional(),
  isActive: z.boolean().default(true),
  customerTierId: customerTierField,
  pickupServiceAreaId: z.union([z.string().optional(), z.literal("none")]),
  deliveryServiceAreaId: z.union([z.string().optional(), z.literal("none")]),
});

export type SpecialHandlingFeeCreateFormSchema = z.infer<
  typeof specialHandlingFeeSchema
>;
export const specialHandlingFeeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum([
    "COOLING",
    "FRAGILE",
    "SIGNATURE_REQUIRED",
    "INSURANCE",
    "LEAVE_AT_DOOR",
  ]),
  fee: z.number().min(0, "Fee must be non-negative"),
  feeScope: z.enum(["DOMESTIC", "INTERNATIONAL", "ANY"]),
  isActive: z.boolean().default(true),
  customerTierId: customerTierField,
});

export type PickupHandlingFeeCreateFormSchema = z.infer<
  typeof pickupHandlingFeeSchema
>;
export const pickupHandlingFeeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  fee: z.number().min(0, "Fee must be non-negative"),
  feeScope: z.enum(["DOMESTIC", "INTERNATIONAL", "ANY"]),
  isActive: z.boolean().default(true),
  customerTierId: customerTierField,
});

export type InternationalBaseFeeCreateFormSchema = z.infer<
  typeof internationalBaseFeeSchema
>;
export const internationalBaseFeeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  originCountry: z.string().min(1, "Origin country is required"),
  destinationCountry: z.string().min(1, "Destination country is required"),
  basePrice: z.number().min(0, "Base price must be non-negative"),
  pickupServiceAreaId: z.string().optional(),
  deliveryServiceAreaId: z.string().optional(),
  isActive: z.boolean().default(true),
  customerTierId: customerTierField,
});

export type InternationalSurchargeFeeCreateFormSchema = z.infer<
  typeof internationalSurchargeFeeSchema
>;
export const internationalSurchargeFeeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  fee: z.number().min(0, "Fee must be non-negative"),
  isActive: z.boolean().default(true),
  customerTierId: customerTierField,
});

export type FeeData =
  | z.infer<typeof domesticBaseFeeSchema>
  | z.infer<typeof speedFeeSchema>
  | z.infer<typeof dimensionalWeightFeeSchema>
  | z.infer<typeof specialHandlingFeeSchema>
  | z.infer<typeof pickupHandlingFeeSchema>
  | z.infer<typeof internationalBaseFeeSchema>
  | z.infer<typeof internationalSurchargeFeeSchema>;

// Create fee mutation
export function useCreateFee(
  selectedFeeType: FeeType,
  {
    onSuccess,
    onError,
  }: { onSuccess?: () => void; onError?: (error: Error) => void } = {}
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Fee, Error, FeeData>({
    mutationFn: async (data) => {
      const endpoints: Record<FeeType, string> = {
        "domestic-base": "domestic-base",
        "dimensional-weight": "dimensional-weight",
        speed: "speed",
        "special-handling": "special-handling",
        "pickup-handling": "pickup-handling",
        "international-base": "international-base",
        "international-surcharge": "international-surcharge",
      };

      const response = await axios.post<Fee>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/${endpoints[selectedFeeType]}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: async () => {
      // Invalidate relevant queries after successful creation
      await queryClient.invalidateQueries({ queryKey: ["fees"] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

export const useGetDomesticBaseFees = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["fees", "domestic-base"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<DomesticBaseFee>>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/domestic-base`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
};

export const useGetSpeedFees = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["fees", "speed"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<SpeedFee>>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/speed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token,
  });
};

export const useGetSpecialHandlingFees = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["fees", "special-handling"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<SpecialHandlingFee>>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/special-handling`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token,
  });
};

export const useGetDimensionalWeightFees = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["fees", "dimensional-weight"],
    queryFn: async () => {
      const { data } = await axios.get<PaginatedResponse<DimensionalWeightFee>>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/dimensional-weight`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token,
  });
};

// Update fee mutation
export function useUpdateFee(
  feeType: FeeType,
  {
    onSuccess,
    onError,
  }: { onSuccess: () => void; onError: (error: Error) => void }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FeeData }) => {
      const { data: responseData } = await axios.patch<Fee>(
        `${import.meta.env.VITE_API_URL}/api/v1/fees/${feeType.toLowerCase()}/${id}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return responseData;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fees", feeType] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error updating fee:", error);
      onError?.(error);
    },
  });
}
